import { Form } from "antd";
import { useEffect, useRef, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { buildPackagePayload } from "features/ProjectType/data/apiAdapters";
import {
  useProjectType,
  useProjectPackage,
  useCreateProjectPackage,
  useUpdateProjectPackage,
  useReloadProjectPackages,
} from "features/ProjectType/data/hooks";
import { setFormApiErrors } from "features/ProjectType/presentation/components/ProjectTypeState";
export default function usePackageForm() {
  const { id, pid } = useParams();
  const history = useHistory();
  const [form] = Form.useForm();
  const type = useProjectType(id);
  const detail = useProjectPackage(pid);
  const create = useCreateProjectPackage();
  const update = useUpdateProjectPackage();
  const reloadPackages = useReloadProjectPackages();
  const mutation = pid ? update : create;
  const [products, setProducts] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [saved, setSaved] = useState(false);
  const [productErrors, setProductErrors] = useState({});
  const initialized = useRef(null);
  useEffect(() => {
    if (detail.data && initialized.current !== pid) {
      initialized.current = pid;
      form.setFieldsValue(detail.data);
      setProducts(detail.data.products || []);
    }
  }, [detail.data, form, pid]);
  useEffect(() => {
    if (saved) history.push(`/setting/project-type/${id}`);
  }, [saved, history, id]);
  useEffect(() => {
    if (!dirty || saved) return;
    const preventUnload = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", preventUnload);
    return () => window.removeEventListener("beforeunload", preventUnload);
  }, [dirty, saved]);
  const save = async (values) => {
    setProductErrors({});
    try {
      const payload = buildPackagePayload({ ...values, products });
      if (pid) await update.mutate(pid, payload);
      else await create.mutate(id, payload);
      setSaved(true);
    } catch (error) {
      setProductErrors(error.fieldErrors || {});
      setFormApiErrors(form, error);
      if (error.code === "PACKAGE_TIER_ALREADY_VISIBLE") {
        // Refresh C11 + C3 per the conflict contract; W2 also fetches both again when mounted.
        await Promise.all([reloadPackages.mutate(id), type.refetch()]).catch(() => undefined);
      }
    }
  };
  return {
    id,
    pid,
    form,
    type,
    detail,
    products,
    setProducts: (value) => {
      setProducts(value);
      setDirty(true);
    },
    dirty: dirty && !saved,
    setDirty,
    save,
    saving: mutation.loading,
    productErrors,
    error: mutation.error?.code === "FORBIDDEN" ? mutation.error : type.error || detail.error,
    loading: type.loading || detail.loading,
    cancel: () => history.push(`/setting/project-type/${id}`),
    refetch: () => {
      type.refetch();
      if (pid) detail.refetch();
    },
  };
}
